import { ChevronDownIcon } from "@chakra-ui/icons";
import {
  Box,
  Button,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { endOfMonth, endOfYear, format, subMonths, subYears } from "date-fns";
import React, { useEffect, useState } from "react";
import { updateCustomerIndvidual } from "../../api/customers";
import { Edit2 } from "react-feather";
import { generateTargets } from "../../api/customerCustomTargets";

const predefinedOptions = [
  { label: "Last Month", value: "last_month" },
  { label: "Year-to-date", value: "year_to_date" },
  { label: "Last Year", value: "last_year" },
];

const getMonthOptions = () => {
  const options = [];
  const now = new Date();
  for (let i = 0; i < 12; i++) {
    const monthDate = subMonths(now, i);
    options.push({
      label: format(monthDate, "MMMM yyyy"),
      value: format(monthDate, "yyyy-MM"),
    });
  }
  return options;
};

const getEndDate = (value) => {
  const now = new Date();
  if (value === "last_month") return endOfMonth(subMonths(now, 1));
  if (value === "year_to_date") return endOfMonth(now);
  if (value === "last_year") return endOfYear(subYears(now, 1));

  if (/^\d{4}-\d{2}$/.test(value)) {
    const parsed = new Date(`${value}-01`);
    return endOfMonth(parsed);
  }

  return null;
};

const ClientCalendarComponent = ({
  id,
  date,
  onDateUpdate = null,
  height = "40px",
}) => {
  const [selected, setSelected] = useState("last_month");
  const [monthOptions, setMonthOptions] = useState(getMonthOptions());
  const [currentDate, setCurrentDate] = useState(date ?? null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setMonthOptions(getMonthOptions());
    if (date) {
      setCurrentDate(new Date(date));
    }
  }, [date]);

  const handleSelect = async (value) => {
    const endDate = getEndDate(value);
    if (!endDate || !id) return;

    setSelected(value);
    const formatted = format(endDate, "yyyy-MM-dd");

    try {
      const formData = new FormData();
      formData.append("dashboard_date", formatted);

      await updateCustomerIndvidual(id, formData);
      await generateTargets(id, formatted);

      setCurrentDate(endDate);
      onDateUpdate?.(formatted);
      setOpen(false);
    } catch (err) {
      console.error("Single update failed", err);
    }
  };

  const getStyles = (isSelected) => ({
    bg: isSelected ? "#536DEC" : "white",
    color: isSelected ? "white" : "black",
    fontWeight: "normal",
    borderRadius: "8px",
    _hover: {
      color: isSelected ? "white" : "#536DEC",
      bg: isSelected ? "#536DEC" : "white",
    },
    _focus: { bg: isSelected ? "#536DEC" : "white" },
  });

  return (
    <>
      {!open && (
        <Tooltip label="Click to edit">
          <HStack
            height={"40px"}
            pt={2}
            onClick={() => {
              setOpen(true);
            }}
          >
            <Text>
              {currentDate ? format(currentDate, "MMM yyyy") : "Date not set"}
            </Text>
            <Edit2 color={"grey"} size={"15px"} />
          </HStack>
        </Tooltip>
      )}
      {open && (
        <Box display="inline-block">
          <Menu>
            <MenuButton
              as={Button}
              rightIcon={<ChevronDownIcon />}
              height={height}
              width="auto"
              bg="white"
              color={currentDate ? "black" : "gray.400"}
              border="1px solid #D1D4DA"
              _hover={{ border: "1px solid #536DEC" }}
              _expanded={{ border: "1px solid #536DEC" }}
            >
              {currentDate ? format(currentDate, "MMM yyyy") : "Date not set"}
            </MenuButton>

            <MenuList width="210px" p={2}>
              <VStack align="start" spacing={1}>
                {predefinedOptions.map((opt) => (
                  <MenuItem
                    key={opt.value}
                    {...getStyles(selected === opt.value)}
                    onClick={() => handleSelect(opt.value)}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
                <Box borderTop="1px solid #E2E8F0" my={1} width="100%" />
                {monthOptions.map((opt) => (
                  <MenuItem
                    key={opt.value}
                    {...getStyles(selected === opt.value)}
                    onClick={() => handleSelect(opt.value)}
                  >
                    {opt.label}
                  </MenuItem>
                ))}
              </VStack>
            </MenuList>
          </Menu>
        </Box>
      )}
    </>
  );
};

export default ClientCalendarComponent;
